import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    username: String,
    slug: String,
    availableDatesUrl: String,
    availableSlotsUrl: String,
    autodetectTimezone: Boolean
  }

  static targets = [
    "leftColumn",
    "rightColumn",
    "calendarDays",
    "calendarLoading",
    "monthYearDisplay",
    "timeSlotsCard",
    "timeSlotsLoading",
    "timeSlotsList",
    "noSlotsMessage",
    "nextButtonContainer",
    "selectedDateDisplay",
    "bookingFormCard",
    "selectedTimeDisplay",
    "selectedTimeInfo",
    "startTimeInput",
    "timezoneInput",
    "timezoneSelect",
    "submitButton",
    "bookingForm"
  ]

  connect() {
    console.log("Scheduling Booking controller connected")

    // Establish the working timezone. The visible selector is the source of
    // truth; it is server-pre-selected to the volunteer's profile zone (task
    // flow) or the booking's stored zone (reschedule). For the anonymous public
    // flow we auto-detect the browser zone when it matches an available option.
    this.timezone = this.currentTimezone()
    if (this.autodetectTimezoneValue) {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (browserTz && this.timezoneOptionExists(browserTz)) {
        this.setTimezone(browserTz)
      }
    }
    // Keep the submitted hidden field in sync with the working timezone.
    this.syncTimezoneInput()

    // Initialize calendar with current month
    this.currentDate = new Date()
    this.selectedDate = null
    this.selectedTime = null
    this.availableDates = []

    // Load current month
    this.loadMonth()

    // Restore selected date and time from form errors
    if (this.hasStartTimeInputTarget && this.startTimeInputTarget.value) {
      const startTime = new Date(this.startTimeInputTarget.value)
      this.selectedDate = startTime.toISOString().split('T')[0]
      this.loadTimeSlots(this.selectedDate)
    }
  }

  // The selector value (if present) is authoritative, else the hidden field,
  // else the browser zone as a last resort.
  currentTimezone() {
    if (this.hasTimezoneSelectTarget && this.timezoneSelectTarget.value) {
      return this.timezoneSelectTarget.value
    }
    if (this.hasTimezoneInputTarget && this.timezoneInputTarget.value) {
      return this.timezoneInputTarget.value
    }
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  }

  timezoneOptionExists(tz) {
    if (!this.hasTimezoneSelectTarget) return false
    return Array.from(this.timezoneSelectTarget.options).some(option => option.value === tz)
  }

  setTimezone(tz) {
    this.timezone = tz
    if (this.hasTimezoneSelectTarget && this.timezoneSelectTarget.value !== tz) {
      this.timezoneSelectTarget.value = tz
    }
    this.syncTimezoneInput()
  }

  syncTimezoneInput() {
    if (this.hasTimezoneInputTarget) {
      this.timezoneInputTarget.value = this.timezone
    }
  }

  // Invoked when the volunteer changes the visible timezone selector. Slot times
  // shift, so clear any in-progress selection and reload the calendar + slots in
  // the new zone. (The selector lives in the calendar column, which is hidden
  // once the booking form is shown, so this only fires during date/time picking.)
  changeTimezone() {
    this.setTimezone(this.timezoneSelectTarget.value)

    this.selectedTime = null
    this.selectedDisplay = null
    if (this.hasStartTimeInputTarget) this.startTimeInputTarget.value = ""
    if (this.hasNextButtonContainerTarget) this.nextButtonContainerTarget.style.display = "none"
    if (this.hasBookingFormCardTarget) this.bookingFormCardTarget.style.display = "none"

    this.loadMonth()
    if (this.selectedDate) {
      this.loadTimeSlots(this.selectedDate)
    }
  }

  // Load available dates for the current month
  loadMonth() {
    const year = this.currentDate.getFullYear()
    const month = this.currentDate.getMonth() + 1

    // Update header
    this.monthYearDisplayTarget.textContent = this.currentDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })

    // Show loading state
    if (this.hasCalendarLoadingTarget) {
      this.calendarLoadingTarget.style.display = 'block'
    }

    // Fetch available dates
    const datesBaseUrl = this.hasAvailableDatesUrlValue && this.availableDatesUrlValue
      ? this.availableDatesUrlValue
      : `/book/${this.bookingUsername}/${this.bookingSlug}/available_dates`
    fetch(`${datesBaseUrl}?year=${year}&month=${month}&timezone=${encodeURIComponent(this.timezone)}`)
      .then(response => response.json())
      .then(data => {
        this.availableDates = data.available_dates
        this.renderCalendar()
        if (this.hasCalendarLoadingTarget) {
          this.calendarLoadingTarget.style.display = 'none'
        }
      })
      .catch(error => {
        console.error('Error loading available dates:', error)
        if (this.hasCalendarLoadingTarget) {
          this.calendarLoadingTarget.style.display = 'none'
        }
      })
  }

  // Render calendar grid
  renderCalendar() {
    const year = this.currentDate.getFullYear()
    const month = this.currentDate.getMonth()

    // Get first day of month and number of days
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    let calendarHTML = ''

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarHTML += '<div class="calendar-day empty"></div>'
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      // Format date as YYYY-MM-DD without timezone conversion
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const isAvailable = this.availableDates.includes(dateStr)
      const isSelected = this.selectedDate === dateStr
      const isPast = date < new Date().setHours(0, 0, 0, 0)

      let classes = 'calendar-day'
      if (isAvailable && !isPast) {
        classes += ' available'
      } else {
        classes += ' disabled'
      }
      if (isSelected) {
        classes += ' selected'
      }

      calendarHTML += `
        <div class="${classes}"
             data-date="${dateStr}"
             data-action="click->scheduling--booking#selectDate">
          ${day}
        </div>
      `
    }

    this.calendarDaysTarget.innerHTML = calendarHTML
  }

  // Navigate to previous month
  previousMonth() {
    this.currentDate.setMonth(this.currentDate.getMonth() - 1)
    this.loadMonth()
  }

  // Navigate to next month
  nextMonth() {
    this.currentDate.setMonth(this.currentDate.getMonth() + 1)
    this.loadMonth()
  }

  // Handle date selection
  selectDate(event) {
    const dayCell = event.currentTarget

    // Don't allow selection of disabled days
    if (dayCell.classList.contains('disabled')) {
      return
    }

    const date = dayCell.dataset.date
    this.selectedDate = date
    this.selectedTime = null

    // Update calendar UI
    this.calendarDaysTarget.querySelectorAll('.calendar-day').forEach(cell => {
      cell.classList.remove('selected')
    })
    dayCell.classList.add('selected')

    // Load time slots for selected date
    this.loadTimeSlots(date)
  }

  // Load available time slots for selected date
  loadTimeSlots(date) {
    if (!date) return

    // Show time slots card and loading state
    this.timeSlotsCardTarget.style.display = 'block'
    this.timeSlotsLoadingTarget.style.display = 'block'
    this.timeSlotsListTarget.innerHTML = ''
    this.noSlotsMessageTarget.style.display = 'none'
    this.nextButtonContainerTarget.style.display = 'none'
    this.bookingFormCardTarget.style.display = 'none'

    // Update selected date display
    // Parse date string (YYYY-MM-DD) without timezone conversion
    const [yearPart, monthPart, dayPart] = date.split('-').map(Number)
    const dateObj = new Date(yearPart, monthPart - 1, dayPart)
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    this.selectedDateDisplayTarget.textContent = formattedDate

    // Fetch available slots
    const slotsBaseUrl = this.hasAvailableSlotsUrlValue && this.availableSlotsUrlValue
      ? this.availableSlotsUrlValue
      : `/book/${this.bookingUsername}/${this.bookingSlug}/slots`
    fetch(`${slotsBaseUrl}?date=${date}&timezone=${encodeURIComponent(this.timezone)}`)
      .then(response => response.json())
      .then(data => {
        this.timeSlotsLoadingTarget.style.display = 'none'

        if (data.slots && data.slots.length > 0) {
          this.displayTimeSlots(data.slots, data.formatted_date)
        } else {
          this.noSlotsMessageTarget.style.display = 'block'
        }
      })
      .catch(error => {
        console.error('Error loading time slots:', error)
        this.timeSlotsLoadingTarget.style.display = 'none'
        this.timeSlotsListTarget.innerHTML = `
          <div class="alert alert-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Error loading available times. Please try again.
          </div>
        `
      })
  }

  // Display time slots as buttons
  displayTimeSlots(slots, formattedDate) {
    this.timeSlotsListTarget.innerHTML = slots.map(slot => {
      return `
        <button type="button"
                class="btn btn-admin-outline-secondary time-slot-button"
                data-time="${slot.time}"
                data-display="${slot.formatted}"
                data-action="click->scheduling--booking#selectTimeSlot">
          ${slot.display}
        </button>
      `
    }).join('')

    // Highlight previously selected time if it exists
    if (this.hasStartTimeInputTarget && this.startTimeInputTarget.value) {
      const selectedTime = this.startTimeInputTarget.value
      const buttons = this.timeSlotsListTarget.querySelectorAll('.time-slot-button')
      buttons.forEach(button => {
        if (button.dataset.time === selectedTime) {
          button.classList.remove('btn-admin-outline-secondary')
          button.classList.add('btn-admin-primary')
        }
      })
    }
  }

  // Handle time slot selection
  selectTimeSlot(event) {
    const button = event.currentTarget
    const time = button.dataset.time
    const display = button.dataset.display

    // Update button states
    const allButtons = this.timeSlotsListTarget.querySelectorAll('.time-slot-button')
    allButtons.forEach(btn => {
      btn.classList.remove('btn-admin-primary')
      btn.classList.add('btn-admin-outline-secondary')
    })
    button.classList.remove('btn-admin-outline-secondary')
    button.classList.add('btn-admin-primary')

    // Store selected time. `display` is server-formatted in the chosen timezone
    // (includes the zone abbreviation), so reuse it verbatim downstream instead
    // of re-deriving from a Date (which would render in the browser zone).
    this.selectedTime = time
    this.selectedDisplay = display
    this.startTimeInputTarget.value = time

    // Show next button
    this.nextButtonContainerTarget.style.display = 'block'

    // Scroll to next button
    this.nextButtonContainerTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  // Proceed to booking form
  proceedToForm() {
    if (!this.selectedTime) return

    // Use the selected calendar date (timezone-agnostic) and the server-formatted
    // time string (already in the chosen timezone, with the zone abbreviation).
    const formattedDate = this.hasSelectedDateDisplayTarget
      ? this.selectedDateDisplayTarget.textContent.trim()
      : ""
    const formattedTime = this.selectedDisplay || ""

    this.selectedTimeDisplayTarget.innerHTML = `
      <div class="d-flex align-items-center">
        <i class="bi bi-calendar-check text-success fs-4 me-3"></i>
        <div>
          <strong>Selected Time:</strong><br>
          ${formattedDate} at ${formattedTime}
        </div>
      </div>
    `

    // Hide left column (calendar) and time slots card
    this.leftColumnTarget.style.display = 'none'
    this.timeSlotsCardTarget.style.display = 'none'

    // Expand right column to full width
    this.rightColumnTarget.classList.remove('col-lg-6')
    this.rightColumnTarget.classList.add('col-lg-12')

    // Show booking form
    this.bookingFormCardTarget.style.display = 'block'

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Return username from Stimulus value attribute, falling back to URL parsing
  get bookingUsername() {
    if (this.hasUsernameValue && this.usernameValue) return this.usernameValue
    return new URL(window.location.href).pathname.split('/')[2]
  }

  // Return slug from Stimulus value attribute, falling back to URL parsing
  get bookingSlug() {
    if (this.hasSlugValue && this.slugValue) return this.slugValue
    return new URL(window.location.href).pathname.split('/')[3]
  }

  // Go back to time selection
  goBack() {
    // Show left column and time slots card
    this.leftColumnTarget.style.display = 'block'
    this.timeSlotsCardTarget.style.display = 'block'

    // Restore right column width
    this.rightColumnTarget.classList.remove('col-lg-12')
    this.rightColumnTarget.classList.add('col-lg-6')

    // Hide booking form
    this.bookingFormCardTarget.style.display = 'none'

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
